import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { usePlatformStore } from "@platform/store";
import {
  joinChimeSession,
  leaveChimeSession,
  stopChimeStreams,
  videoEventBus,
} from "@core/features/video";
import type { MeetingMode } from "@core/features/video";
import {
  joinVideoMeetingApi,
  leaveVideoMeeting as leaveVideoMeetingApi,
} from "@core/features/videoMeeting";
import type { MeetingDetails } from "@core/features/videoMeeting";
import { logMeetingEvent } from "@core/features/videoMeeting/utils/logMeetingEvent";
import { useDevices } from "@platform/features/video/providers/DevicesProvider";

type UseJoinVideoMeetingArgs = {
  meetingId: string | null;
  roomId: number | null;
  meetingDetails: MeetingDetails | null;
  region: string;
  enableSimulcast: boolean;
  priorityBasedPolicy?: any;
  keepLastFrameWhenPaused: boolean;
  isEchoReductionEnabled: boolean;
  // Store setters
  setMeetingId: (id: string | null) => void;
  setRoomId: (id: number | null) => void;
  setInitialized: (initialized: boolean) => void;
  setRegion: (region: string) => void;
  setMeetingMode: (mode: MeetingMode) => void;
  setSessionError: (error: string | null) => void;
  setRoomMembersData: (data: any) => void;
  setChimeExternalUserId: (id: string | null) => void;
  setUserName: (name: string | null) => void;
  resetVideo: () => void;
  resetVideoMeeting: () => void;
};

export function useJoinVideoMeeting({
  meetingId,
  roomId,
  meetingDetails,
  region,
  enableSimulcast,
  priorityBasedPolicy,
  keepLastFrameWhenPaused,
  isEchoReductionEnabled,
  setMeetingId,
  setRoomId,
  setInitialized,
  setRegion,
  setMeetingMode,
  setSessionError,
  setRoomMembersData,
  setChimeExternalUserId,
  setUserName,
  resetVideo,
  resetVideoMeeting,
}: UseJoinVideoMeetingArgs) {
  const meetingManager = useMeetingManager();
  const navigateRef = useRef(useNavigate());
  const user = usePlatformStore((s) => s.user);
  const chimeExternalUserId = usePlatformStore((s) => s.chimeExternalUserId);
  const selectedAudioInputDevice = usePlatformStore((s) => s.selectedAudioInputDevice);
  const selectedVideoInputDevice = usePlatformStore((s) => s.selectedVideoInputDevice);
  const selectedAudioOutputDevice = usePlatformStore((s) => s.selectedAudioOutputDevice);
  const {
    audioInputDevices,
    videoInputDevices,
    audioOutputDevices,
  } = useDevices();
  const audioInputDevicesRef = useRef(audioInputDevices);
  audioInputDevicesRef.current = audioInputDevices;
  const videoInputDevicesRef = useRef(videoInputDevices);
  videoInputDevicesRef.current = videoInputDevices;
  const audioOutputDevicesRef = useRef(audioOutputDevices);
  audioOutputDevicesRef.current = audioOutputDevices;

  // Join meeting: call API + Chime session + set devices
  const joinMeeting = useCallback(async () => {
    try {
      setSessionError(null);

      if (!meetingDetails) {
        throw new Error("No meeting details available");
      }

      if (!meetingDetails.video_rooms?.[0]) {
        throw new Error("No video room available for this meeting");
      }

      // Call backend join API
      const joinResponse = await joinVideoMeetingApi(
        {
          meeting_id: meetingDetails.id,
          region,
          echo_reduction: isEchoReductionEnabled,
        },
        user ? { id: user.id } : null
      );

      const chimeMeetingId = joinResponse.chime_meeting_id;

      // Join Chime session
      await joinChimeSession({
        joinInfo: joinResponse.join_info,
        enableSimulcast,
        priorityBasedPolicy,
        keepLastFrameWhenPaused,
        meetingManager,
        setRegion,
        setMeetingMode,
      });

      // Start devices after joining — delay needed for Chime SDK internal
      // initialization to complete before device selection. Removing this
      // causes intermittent device binding failures.
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Use joinResponse data directly — store isn't updated yet at this point
      const deviceEventCtx = () => ({
        meeting_id: meetingDetails.id,
        room_id: joinResponse.room_id,
        user: user ? { id: user.id } : null,
      });

      // Build device label lookup from DevicesProvider refs (always current)
      const deviceLabelMap: Record<string, string> = {};
      for (const d of [...audioInputDevicesRef.current, ...videoInputDevicesRef.current, ...audioOutputDevicesRef.current]) {
        if (d.deviceId && d.label) deviceLabelMap[d.deviceId] = d.label;
      }
      const labelFor = (id: string | null) => (id ? deviceLabelMap[id] ?? id : 'default');

      // Start audio input: fall back to 'default' for iOS browsers (e.g.
      // DuckDuckGo) that return empty deviceIds
      try {
        await meetingManager.startAudioInputDevice(selectedAudioInputDevice || 'default');
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_initial', device_type: 'audio_input', device_id: selectedAudioInputDevice || 'default', device_label: labelFor(selectedAudioInputDevice) });
      } catch (e) {
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_failed', device_type: 'audio_input', device_id: selectedAudioInputDevice, device_label: labelFor(selectedAudioInputDevice), error_name: (e as Error)?.name, error_message: (e as Error)?.message });
        console.warn('[join] Audio input start failed, retrying with default:', e);
        try {
          await meetingManager.startAudioInputDevice('default');
          logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_fallback', device_type: 'audio_input', original_device_id: selectedAudioInputDevice, original_label: labelFor(selectedAudioInputDevice), fallback: 'default' });
        } catch (e2) { console.warn('[join] Audio input fallback also failed:', e2); }
      }
      // Start video: try selected device, fall back to 'default' for iOS
      // browsers (e.g. DuckDuckGo) that return empty deviceIds
      try {
        await meetingManager.startVideoInputDevice(selectedVideoInputDevice || 'default');
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_initial', device_type: 'video_input', device_id: selectedVideoInputDevice || 'default', device_label: labelFor(selectedVideoInputDevice) });
      } catch (e) {
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_failed', device_type: 'video_input', device_id: selectedVideoInputDevice, device_label: labelFor(selectedVideoInputDevice), error_name: (e as Error)?.name, error_message: (e as Error)?.message });
        console.warn('[join] Video input start failed, retrying with default:', e);
        try {
          await meetingManager.startVideoInputDevice('default');
          logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_fallback', device_type: 'video_input', original_device_id: selectedVideoInputDevice, original_label: labelFor(selectedVideoInputDevice), fallback: 'default' });
        } catch (e2) { console.warn('[join] Video input fallback also failed:', e2); }
      }
      // Start audio output: same fallback pattern
      try {
        await meetingManager.startAudioOutputDevice(selectedAudioOutputDevice || 'default');
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_initial', device_type: 'audio_output', device_id: selectedAudioOutputDevice || 'default', device_label: labelFor(selectedAudioOutputDevice) });
      } catch (e) {
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_failed', device_type: 'audio_output', device_id: selectedAudioOutputDevice, device_label: labelFor(selectedAudioOutputDevice), error_name: (e as Error)?.name, error_message: (e as Error)?.message });
        console.warn('[join] Audio output start failed, retrying with default:', e);
        try {
          await meetingManager.startAudioOutputDevice('default');
          logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_fallback', device_type: 'audio_output', original_device_id: selectedAudioOutputDevice, original_label: labelFor(selectedAudioOutputDevice), fallback: 'default' });
        } catch (e2) { console.warn('[join] Audio output fallback also failed:', e2); }
      }

      // Tell the encoder to drop framerate before resolution under bandwidth
      // pressure. Pairs with the raised Hi-layer ceiling (set on the simulcast
      // uplink policy in joinChimeSession): at the configured bitrate the
      // encoder produces 720p, and if the link can't sustain it, we'd rather
      // lose fps than pixels.
      //
      // degradationPreference is a top-level RTCRtpSender parameter (not in
      // the encoding map), so it survives the simulcast policy's resubscribe
      // clobber. setParameters can reject on older Safari versions that don't
      // accept the 'maintain-resolution' enum value — non-fatal via .catch();
      // encoder defaults to 'maintain-framerate', which is the existing
      // behavior. The Chime SDK doesn't expose RTCPeerConnection on its
      // public AudioVideoFacade type — the controller's rtcPeerConnection
      // getter is the access path. If the SDK's internal layout shifts,
      // optional chains return undefined and nothing fires.
      const av = meetingManager.meetingSession?.audioVideo as unknown as
        | { audioVideoController?: { rtcPeerConnection?: RTCPeerConnection | null } }
        | undefined;
      const pc = av?.audioVideoController?.rtcPeerConnection;
      const videoSender = pc?.getSenders?.().find((s) => s.track?.kind === "video");
      if (videoSender) {
        const params = videoSender.getParameters();
        videoSender
          .setParameters({ ...params, degradationPreference: "maintain-resolution" })
          .catch(() => {
            // Older Safari / sender not yet bound / SDK access shift — non-fatal.
          });
      }

      // Update store state
      if (chimeMeetingId !== meetingId) {
        setMeetingId(chimeMeetingId);
      }
      if (joinResponse.room_id) {
        setRoomId(joinResponse.room_id);
      }

      // Store the external user ID for multi-tab leave safety
      const externalUserId = joinResponse.join_info?.Attendee?.ExternalUserId || null;
      if (externalUserId) {
        setChimeExternalUserId(externalUserId);
      }

      // Store room members (slice handles array-to-record conversion)
      if (joinResponse.room_members) {
        setRoomMembersData(joinResponse.room_members);
      }

      // Persist session info to sessionStorage (not Zustand persist) so
      // clearExistingSession can clean up after a page refresh. We use
      // sessionStorage rather than the Zustand persist layer because:
      // - sessionStorage auto-clears on tab close (correct lifecycle)
      // - Zustand persist only allows user/authToken; adding video state
      //   would rehydrate stale meeting IDs on every app load
      sessionStorage.setItem(
        "video_session",
        JSON.stringify({
          roomId: joinResponse.room_id,
          meetingId: chimeMeetingId,
          chimeExternalUserId: externalUserId,
        })
      );

      setInitialized(true);

      if (user) {
        setUserName(`${user.first_name} ${user.last_name}`.trim() || null);
      }

      if (chimeMeetingId && user?.id) {
        videoEventBus.emitEvent("videochat:joined", {
          meetingId: chimeMeetingId,
        });
      }

      // Navigate to meeting page
      navigateRef.current(`/video/meeting/${meetingDetails.id}`);
    } catch (err) {
      console.error("[useJoinVideoMeeting] Join failed:", err);
      setSessionError(err instanceof Error ? err.message : String(err));
      setInitialized(false);
    }
  }, [
    meetingId,
    meetingDetails,
    region,
    enableSimulcast,
    priorityBasedPolicy,
    keepLastFrameWhenPaused,
    isEchoReductionEnabled,
    meetingManager,
    selectedAudioInputDevice,
    selectedVideoInputDevice,
    selectedAudioOutputDevice,
    setMeetingId,
    setRoomId,
    setInitialized,
    setRegion,
    setMeetingMode,
    setSessionError,
    setRoomMembersData,
    setChimeExternalUserId,
    setUserName,
    user,
  ]);

  // Leave meeting: cleanup streams + API + Chime leave
  const leaveMeeting = useCallback(async () => {
    try {
      setSessionError(null);

      // Send camera-off intent BEFORE teardown so remote participants
      // can use the short roster grace (ROSTER_LEAVE_GRACE_MS).
      // This must be imperative, not via React effects, because the
      // data channel closes during leaveChimeSession().
      try {
        const audioVideo = meetingManager.meetingSession?.audioVideo;
        if (audioVideo) {
          audioVideo.realtimeSendDataMessage(
            "device-state",
            JSON.stringify({
              camera: false,
              mic: false,
              timestamp: Date.now(),
            }),
            30_000
          );
          // Small delay to let the data message reach the SFU before teardown
          await new Promise((r) => setTimeout(r, 100));
        }
      } catch {
        // Best-effort — if this fails, remote will use 30s grace
      }

      await stopChimeStreams(meetingManager);

      if (roomId) {
        try {
          await leaveVideoMeetingApi(roomId, chimeExternalUserId);
        } catch {
          // Non-fatal
        }
      }

      if (meetingId) {
        await leaveChimeSession(meetingManager);
        videoEventBus.emitEvent("videochat:left", { meetingId });
      }

      resetVideo();
      resetVideoMeeting();
      sessionStorage.removeItem("video_session");
    } catch (err) {
      const errorMessage = (err as Error).message;
      setSessionError(errorMessage);
      resetVideo();
      resetVideoMeeting();
      sessionStorage.removeItem("video_session");
      throw err;
    }
  }, [
    meetingId,
    roomId,
    chimeExternalUserId,
    meetingManager,
    setSessionError,
    resetVideo,
    resetVideoMeeting,
  ]);

  // Clear existing session (used when revisiting join page).
  // After a page refresh, Zustand video state resets to null/false,
  // so we fall back to sessionStorage for roomId/meetingId to ensure
  // backend leave API and Chime cleanup still run.
  const clearExistingSession = useCallback(async () => {
    const stored = sessionStorage.getItem("video_session");
    let fallback = null;
    try { fallback = stored ? JSON.parse(stored) : null; } catch {}
    const effectiveRoomId = roomId ?? fallback?.roomId;
    const effectiveMeetingId = meetingId ?? fallback?.meetingId;
    const effectiveExternalUserId = chimeExternalUserId ?? fallback?.chimeExternalUserId;

    if (!effectiveRoomId && !effectiveMeetingId) return;

    try {
      if (effectiveRoomId) {
        try {
          await leaveVideoMeetingApi(effectiveRoomId, effectiveExternalUserId);
        } catch {
          // Non-fatal
        }
      }
      if (effectiveMeetingId) {
        await leaveChimeSession(meetingManager);
      }
    } catch {
      // Silently handle cleanup errors
    }

    sessionStorage.removeItem("video_session");
    setInitialized(false);
  }, [meetingId, roomId, chimeExternalUserId, meetingManager, setInitialized]);

  return {
    meetingManager,
    joinMeeting,
    leaveMeeting,
    clearExistingSession,
  };
}
